<template>
  <transition name="fade">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        
        <!-- Brand Logo & Name -->
        <div class="brand-header">
          <svg class="brand-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
          </svg>
          <h1 class="brand-name">PA Tours</h1>
        </div>

        <!-- Tagline -->
        <p class="tagline">Your Journey, Our Passion</p>

        <!-- Brief Description -->
        <div class="brief-section">
          <p class="brief-text">
            Crafting unforgettable travel experiences across Kerala's 
            backwaters, hills, and beaches. Premium hotels, flights, 
            and personalized tour packages tailored just for you.
          </p>
        </div>

        <!-- Progress Line Loader -->
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill"></div>
          </div>
          <p class="loading-text">Preparing your experience...</p>
        </div>

        <!-- Services Icons -->
        <div class="services-row">
          <div class="service-item">
            <span class="service-icon">✈️</span>
            <span class="service-label">Flights</span>
          </div>
          <div class="service-item">
            <span class="service-icon">🏨</span>
            <span class="service-label">Hotels</span>
          </div>
          <div class="service-item">
            <span class="service-icon">🚗</span>
            <span class="service-label">Cabs</span>
          </div>
          <div class="service-item">
            <span class="service-icon">🎒</span>
            <span class="service-label">Tours</span>
          </div>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'LoadingSpinner',
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.loading-content {
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  text-align: center;
}

/* Brand Header */
.brand-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.brand-icon {
  width: 36px;
  height: 36px;
  color: #000000;
}

.brand-name {
  font-size: 36px;
  font-weight: 700;
  color: #000000;
  margin: 0;
  letter-spacing: -1px;
}

/* Tagline */
.tagline {
  font-size: 16px;
  font-weight: 600;
  color: #666666;
  margin: 0;
  letter-spacing: 0.3px;
}

/* Brief Section */
.brief-section {
  padding: 0 20px;
}

.brief-text {
  font-size: 14px;
  line-height: 1.6;
  color: #666666;
  margin: 0;
}

/* Progress Container */
.progress-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

/* Progress Bar */
.progress-bar {
  width: 100%;
  height: 4px;
  background: #f0f0f0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #000000 0%, #4a4a4a 50%, #000000 100%);
  background-size: 200% 100%;
  animation: loadProgress 2s ease-in-out infinite;
  border-radius: 2px;
}

@keyframes loadProgress {
  0% {
    width: 0%;
    background-position: 0% 50%;
  }
  50% {
    width: 70%;
    background-position: 100% 50%;
  }
  100% {
    width: 100%;
    background-position: 200% 50%;
  }
}

/* Loading Text */
.loading-text {
  font-size: 13px;
  font-weight: 600;
  color: #999999;
  margin: 0;
  letter-spacing: 0.3px;
}

/* Services Row */
.services-row {
  display: flex;
  justify-content: center;
  gap: 32px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.service-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: 0;
  animation: fadeInUp 0.6s ease-out forwards;
}

.service-item:nth-child(1) {
  animation-delay: 0.2s;
}

.service-item:nth-child(2) {
  animation-delay: 0.4s;
}

.service-item:nth-child(3) {
  animation-delay: 0.6s;
}

.service-item:nth-child(4) {
  animation-delay: 0.8s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.service-icon {
  font-size: 28px;
}

.service-label {
  font-size: 12px;
  font-weight: 600;
  color: #666666;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Tablet Responsive */
@media (max-width: 768px) {
  .loading-content {
    max-width: 480px;
    gap: 20px;
  }

  .brand-icon {
    width: 32px;
    height: 32px;
  }

  .brand-name {
    font-size: 32px;
  }

  .tagline {
    font-size: 15px;
  }

  .brief-text {
    font-size: 13px;
  }

  .services-row {
    gap: 24px;
  }

  .service-icon {
    font-size: 26px;
  }
}

/* Mobile Responsive */
@media (max-width: 480px) {
  .loading-content {
    gap: 18px;
    padding: 0 12px;
  }

  .brand-header {
    gap: 10px;
  }

  .brand-icon {
    width: 28px;
    height: 28px;
  }

  .brand-name {
    font-size: 28px;
  }

  .tagline {
    font-size: 14px;
  }

  .brief-section {
    padding: 0 8px;
  }

  .brief-text {
    font-size: 12px;
    line-height: 1.5;
  }

  .progress-bar {
    height: 3px;
  }

  .loading-text {
    font-size: 12px;
  }

  .services-row {
    gap: 20px;
  }

  .service-icon {
    font-size: 24px;
  }

  .service-label {
    font-size: 11px;
  }
}

/* Extra Small Mobile */
@media (max-width: 360px) {
  .brand-icon {
    width: 24px;
    height: 24px;
  }

  .brand-name {
    font-size: 24px;
  }

  .tagline {
    font-size: 13px;
  }

  .brief-text {
    font-size: 11px;
  }

  .services-row {
    gap: 16px;
  }

  .service-icon {
    font-size: 22px;
  }
}
</style>
